/**
 * OTP Verification Page
 * Handles OTP entry and verification
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import './LoginPage.css';

function VerifyOtpPage() {
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [resendCooldown, setResendCooldown] = useState(0);
    const [resendLoading, setResendLoading] = useState(false);
    
    const { verifyOtp, resendOtp } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    
    const userId = location.state?.user_id || sessionStorage.getItem('pending_user_id');
    const inputRefs = useRef([]);

    // Redirect if no user_id
    useEffect(() => {
        if (!userId) {
            navigate('/login');
        }
    }, [userId, navigate]);

    // Resend cooldown timer
    useEffect(() => {
        if (resendCooldown > 0) {
            const timer = setTimeout(() => {
                setResendCooldown(resendCooldown - 1);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [resendCooldown]);

    // Handle OTP input
    const handleInputChange = (index, value) => {
        if (value.length > 1) return; // Only single character
        
        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        // Auto-focus next input
        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    // Handle backspace
    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    // Handle paste
    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').slice(0, 6);
        const digits = pastedData.split('').filter(d => /\d/.test(d));
        
        const newOtp = [...otp];
        digits.forEach((digit, i) => {
            if (i < 6) newOtp[i] = digit;
        });
        setOtp(newOtp);
        
        // Focus on the next empty input
        const nextIndex = Math.min(digits.length, 5);
        inputRefs.current[nextIndex]?.focus();
    };

    // Submit OTP
    const handleSubmit = async (e) => {
        e.preventDefault();
        const otpValue = otp.join('');
        
        if (otpValue.length !== 6) {
            setError('Please enter all 6 digits');
            return;
        }

        setLoading(true);
        setError('');

        try {
            await verifyOtp(parseInt(userId), otpValue);
            navigate('/');
        } catch (err) {
            setError(err.message);
            // Clear OTP inputs on error
            setOtp(['', '', '', '', '', '']);
            inputRefs.current[0]?.focus();
        } finally {
            setLoading(false);
        }
    };

    // Resend OTP
    const handleResend = async () => {
        if (resendCooldown > 0 || resendLoading) return;
        
        setResendLoading(true);
        setError('');

        try {
            await resendOtp(parseInt(userId));
            setResendCooldown(60); // 60 seconds cooldown
            setOtp(['', '', '', '', '', '']);
            inputRefs.current[0]?.focus();
        } catch (err) {
            setError(err.message);
        } finally {
            setResendLoading(false);
        }
    };

    // Go back to login
    const handleBack = () => {
        sessionStorage.removeItem('pending_user_id');
        navigate('/login');
    };

    if (!userId) {
        return null;
    }

    return (
        <div className="login-container">
            <div className="login-card">
                <div className="login-header">
                    <h1>Verify Your Email</h1>
                    <p>Enter the 6-digit code sent to your email</p>
                </div>

                {error && (
                    <div className="alert alert-error">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="login-form">
                    <div className="otp-inputs">
                        {otp.map((digit, index) => (
                            <input
                                key={index}
                                ref={el => inputRefs.current[index] = el}
                                type="text"
                                inputMode="numeric"
                                maxLength={1}
                                value={digit}
                                onChange={(e) => handleInputChange(index, e.target.value)}
                                onKeyDown={(e) => handleKeyDown(index, e)}
                                onPaste={handlePaste}
                                disabled={loading}
                            />
                        ))}
                    </div>

                    <button 
                        type="submit" 
                        className="btn btn-primary btn-full"
                        disabled={loading || otp.join('').length !== 6}
                    >
                        {loading ? 'Verifying...' : 'Verify & Continue'}
                    </button>
                </form>

                <div className="login-footer">
                    <p>
                        Didn't receive the code?{' '}
                        <button 
                            onClick={handleResend}
                            disabled={resendCooldown > 0 || resendLoading}
                            className="link-button"
                        >
                            {resendCooldown > 0 
                                ? `Resend in ${resendCooldown}s` 
                                : resendLoading 
                                    ? 'Sending...' 
                                    : 'Resend OTP'
                            }
                        </button>
                    </p>
                    <p style={{ marginTop: '12px' }}>
                        <button onClick={handleBack} className="link-button">
                            ← Back to Login
                        </button>
                    </p>
                </div>
            </div>
        </div>
    );
}

export default VerifyOtpPage;
